'use client';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  FC,
  ReactNode,
} from 'react';
import { isValidElement } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components/Button';
import { Button as AriaButton } from 'react-aria-components/Button';
import { Link as AriaLink } from 'react-aria-components/Link';

import { cx, sortCx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/is-react-component';

/**
 * @fileoverview Button component for the TRM UI Kit.
 * @agents generated no-review
 *
 * A versatile, accessible button component built on React Aria Components with comprehensive
 * styling variants, loading states, and support for both button and anchor elements.
 * This component is generated from UntitledUI with TRM-specific customizations.
 *
 * @see https://www.untitledui.com/components - UntitledUI Component Library
 * @see https://react-spectrum.adobe.com/react-aria/Button.html - React Aria Button
 *
 * @warning This file contains generated code from UntitledUI. Manual changes should be
 * documented and carefully considered as they may be affected by future regenerations.
 *
 * @deviation `textProps` prop was added manually (not from UntitledUI baseline) to forward
 * `data-*` attributes to the inner `<span data-text>` for HEAP analytics and test targeting.
 * Component-controlled attributes (`data-text`, `className`) always take precedence over
 * anything passed via `textProps`. See {@link TextSpanDataProps}.
 *
 * @example
 * ```tsx
 * // Basic button
 * <Button>Click me</Button>
 *
 * // Primary button with loading state
 * <Button color="primary" size="md" isLoading>
 *   Saving...
 * </Button>
 *
 * // Link-styled button
 * <Button color="link-color" href="/dashboard">
 *   Go to Dashboard
 * </Button>
 *
 * // Button with icons
 * <Button iconLeading={PlusIcon} iconTrailing={ArrowRightIcon}>
 *   Add Item
 * </Button>
 * ```
 */

export const styles = sortCx({
  common: {
    root: [
      'group relative inline-flex h-max cursor-pointer items-center justify-center whitespace-nowrap outline-brand transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:outline-offset-2',
      // When button is used within `InputGroup`
      'in-data-input-wrapper:shadow-xs in-data-input-wrapper:focus:!z-50 in-data-input-wrapper:in-data-leading:-mr-px in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-leading:before:rounded-r-none in-data-input-wrapper:in-data-trailing:-ml-px in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-trailing:before:rounded-l-none',
      // Disabled styles
      'disabled:cursor-not-allowed disabled:text-fg-disabled',
      // Icon styles
      'disabled:*:data-icon:text-fg-disabled_subtle',
      // Same as `icon` but for SSR icons that cannot be passed to the client as functions.
      '*:data-icon:pointer-events-none *:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:transition-inherit-all',
    ].join(' '),
    icon: 'pointer-events-none size-5 shrink-0 transition-inherit-all',
  },
  sizes: {
    sm: {
      root: [
        'gap-1 rounded-lg px-3 py-2 text-sm font-semibold before:rounded-[7px] data-icon-only:p-2',
        'in-data-input-wrapper:px-3.5 in-data-input-wrapper:py-2.5 in-data-input-wrapper:data-icon-only:p-2.5',
      ].join(' '),
      linkRoot: 'gap-1',
    },
    md: {
      root: [
        'gap-1 rounded-lg px-3.5 py-2.5 text-sm font-semibold before:rounded-[7px] data-icon-only:p-2.5',
        'in-data-input-wrapper:gap-1.5 in-data-input-wrapper:px-4 in-data-input-wrapper:text-md in-data-input-wrapper:data-icon-only:p-3',
      ].join(' '),
      linkRoot: 'gap-1',
    },
    lg: {
      root: 'gap-1.5 rounded-lg px-4 py-2.5 text-md font-semibold before:rounded-[7px] data-icon-only:p-3',
      linkRoot: 'gap-1.5',
    },
    xl: {
      root: 'gap-1.5 rounded-lg px-4.5 py-3 text-md font-semibold before:rounded-[7px] data-icon-only:p-3.5',
      linkRoot: 'gap-1.5',
    },
  },

  colors: {
    primary: {
      root: [
        'bg-brand-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover data-loading:bg-brand-solid_hover',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-primary-icon hover:*:data-icon:text-button-primary-icon_hover',
      ].join(' '),
    },
    secondary: {
      root: [
        'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover data-loading:bg-primary_hover',
        // Disabled styles
        'disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    tertiary: {
      root: [
        'text-tertiary hover:bg-primary_hover hover:text-tertiary_hover data-loading:bg-primary_hover',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    'link-gray': {
      root: [
        'justify-normal rounded p-0! text-tertiary hover:text-tertiary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-quaternary hover:*:data-icon:text-fg-quaternary_hover',
      ].join(' '),
    },
    'link-color': {
      root: [
        'justify-normal rounded p-0! text-brand-secondary hover:text-brand-secondary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-brand-secondary_alt hover:*:data-icon:text-fg-brand-secondary_hover',
      ].join(' '),
    },
    'primary-destructive': {
      root: [
        'bg-error-solid text-white shadow-xs-skeumorphic ring-1 ring-transparent outline-error ring-inset',
        // Inner border gradient
        'before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
        // Disabled styles
        'disabled:bg-disabled disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-button-destructive-primary-icon hover:*:data-icon:text-button-destructive-primary-icon_hover',
      ].join(' '),
    },
    'secondary-destructive': {
      root: [
        'bg-primary text-error-primary shadow-xs-skeumorphic ring-1 ring-error_subtle outline-error ring-inset hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
        // Disabled styles
        'disabled:bg-primary disabled:shadow-xs disabled:ring-disabled_subtle',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
    'tertiary-destructive': {
      root: [
        'text-error-primary outline-error hover:bg-error-primary hover:text-error-primary_hover data-loading:bg-error-primary',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
    'link-destructive': {
      root: [
        'justify-normal rounded p-0! text-error-primary outline-error hover:text-error-primary_hover',
        // Inner text underline
        '*:data-text:underline *:data-text:decoration-transparent *:data-text:underline-offset-2 hover:*:data-text:decoration-current',
        // Icon styles
        '*:data-icon:text-fg-error-secondary hover:*:data-icon:text-fg-error-primary',
      ].join(' '),
    },
  },
});

/**
 * Restricts props forwarded to the inner `<span data-text>` to `data-*` attributes only.
 *
 * Intentionally excludes `className`, `children`, event handlers, and all other
 * non-data attributes to keep the API focused on analytics/test-selector use cases.
 * `data-text` is also excluded because it is component-controlled and must not be overridden.
 *
 * @public
 */
export type TextSpanDataProps = Omit<
  { [K in `data-${string}`]?: string | boolean | number | null | undefined },
  'data-text'
>;

/**
 * Common properties shared by both button and link button variants.
 *
 * @public
 */
interface CommonProps {
  /**
   * Whether the button is disabled. When true, prevents user interaction and applies
   * disabled styling. For link buttons, this removes the href to prevent navigation.
   *
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Whether the button is in a loading state. When true, shows a loading spinner
   * and optionally hides content based on `showTextWhileLoading`.
   *
   * @default false
   */
  isLoading?: boolean;
  /**
   * Size variant of the button. Controls padding, font size, and icon spacing.
   *
   * @default "sm"
   */
  size?: keyof typeof styles.sizes;
  /**
   * Color variant of the button. Determines the visual style including background,
   * text color, and hover states.
   *
   * @default "primary"
   */
  color?: keyof typeof styles.colors;
  /**
   * Leading icon component or React node displayed before the button text.
   * Accepts a React component function or a ReactNode element.
   */
  iconLeading?: FC<{ className?: string }> | ReactNode;
  /**
   * Trailing icon component or React node displayed after the button text.
   * Accepts a React component function or a ReactNode element.
   */
  iconTrailing?: FC<{ className?: string }> | ReactNode;
  /**
   * Whether to remove horizontal padding from the text content. Automatically
   * set to true for link-type colors (link-gray, link-color, link-destructive).
   *
   * @default false
   */
  noTextPadding?: boolean;
  /**
   * Whether to show text content while in loading state. When false (default),
   * text is hidden during loading; when true, text remains visible alongside
   * the loading spinner.
   *
   * @default false
   */
  showTextWhileLoading?: boolean;
  /**
   * Additional `data-*` attributes forwarded exclusively to the inner `<span data-text>` node.
   *
   * Intended for analytics platforms (e.g. HEAP) and test automation that need to target the
   * label span directly rather than the root button element. Only `data-*` keys are accepted;
   * `className`, `children`, event handlers, and `data-text` are disallowed by the type.
   *
   * Note: attributes passed through normal `...props` spread still land on the root element
   * as usual — `textProps` is the only way to reach the inner span.
   *
   * @example
   * ```tsx
   * <Button textProps={{ 'data-testid': 'button-text-foo' }}>Label</Button>
   * ```
   */
  textProps?: TextSpanDataProps;
}

/**
 * Props for button element variant of the Button component.
 *
 * Extends all standard HTML button attributes (except `color` and `slot` which are
 * overridden) and includes React Aria Components slot support for composition.
 *
 * @public
 * @example
 * ```tsx
 * <Button
 *   type="submit"
 *   onClick={handleClick}
 *   size="md"
 *   color="primary"
 *   isLoading={isSubmitting}
 * >
 *   Submit Form
 * </Button>
 * ```
 */
export interface ButtonProps
  extends
    CommonProps,
    DetailedHTMLProps<
      Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>,
      HTMLButtonElement
    > {
  /**
   * React Aria Components slot identifier for composition patterns.
   * Allows the button to be identified within a component tree.
   */
  slot?: AriaButtonProps['slot'];
}

/**
 * Props for anchor element variant of the Button component.
 *
 * Extends all standard HTML anchor attributes (except `color` which is overridden)
 * and enables button styling on links. The component automatically renders as a
 * link when an `href` prop is provided.
 *
 * @public
 * @example
 * ```tsx
 * <Button
 *   href="/dashboard"
 *   color="link-color"
 *   iconTrailing={ArrowRightIcon}
 * >
 *   Go to Dashboard
 * </Button>
 * ```
 */
export interface LinkProps
  extends
    CommonProps,
    DetailedHTMLProps<Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>, HTMLAnchorElement> {}

/**
 * Union type representing all valid props for the Button component.
 *
 * The component automatically determines whether to render as a button or anchor
 * element based on the presence of an `href` prop. When `href` is provided,
 * the component uses `LinkProps` and renders an anchor; otherwise, it uses
 * `ButtonProps` and renders a button element.
 *
 * @public
 * @example
 * ```tsx
 * // Renders as button (no href)
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // Renders as link (href provided)
 * <Button href="/page">Go to page</Button>
 * ```
 */
export type Props = ButtonProps | LinkProps;

/**
 * Versatile button component supporting both button and link variants with comprehensive
 * styling options, loading states, and icon support.
 *
 * @param {Props} props - Button properties.
 * @remarks
 * Automatically renders as an anchor element when `href` is provided, otherwise renders as a button.
 * Built on React Aria Components for accessibility. See {@link ButtonProps} for button variant
 * props and {@link LinkProps} for link variant props. Both extend {@link CommonProps}.
 * @public
 * @example <Button color="primary" size="md" onClick={handleClick}>Click me</Button>
 */
export const Button = ({
  size = 'sm',
  color = 'primary',
  children,
  className,
  noTextPadding,
  iconLeading: IconLeading,
  iconTrailing: IconTrailing,
  isDisabled: disabled,
  isLoading: loading,
  showTextWhileLoading,
  textProps,
  ...otherProps
}: Props) => {
  const href = 'href' in otherProps ? otherProps.href : undefined;
  const Component = href ? AriaLink : AriaButton;

  const isIcon = (IconLeading || IconTrailing) && !children;
  const isLinkType = ['link-gray', 'link-color', 'link-destructive'].includes(color);

  noTextPadding = isLinkType || noTextPadding;

  let props = {};

  if (href) {
    props = {
      ...otherProps,

      href: disabled ? undefined : href,

      // Since anchor elements do not support the `disabled` attribute and state,
      // we need to specify `data-rac` and `data-disabled` in order to be able
      // to use the `disabled:` selector in classes.
      ...(disabled ? { 'data-rac': true, 'data-disabled': true } : {}),
    };
  } else {
    props = {
      ...otherProps,

      type: otherProps.type || 'button',
      isPending: loading,
      isDisabled: disabled,
    };
  }
    
   const safeTextProps = Object.fromEntries(
     Object.entries(textProps ?? {}).filter(
       ([key]) => key.startsWith('data-') && key !== 'data-text',
     ),
   ) as TextSpanDataProps;

  return (
    <Component
      data-loading={loading ? true : undefined}
      data-icon-only={isIcon ? true : undefined}
      {...props}
      className={cx(
        styles.common.root,
        styles.sizes[size].root,
        styles.colors[color].root,
        isLinkType && styles.sizes[size].linkRoot,
        (loading || (href && (disabled || loading))) && 'pointer-events-none',
        // If in `loading` state, hide everything except the loading icon (and text if `showTextWhileLoading` is true).
        loading &&
          (showTextWhileLoading
            ? '[&>*:not([data-icon=loading]):not([data-text])]:hidden'
            : '[&>*:not([data-icon=loading])]:invisible'),
        className,
      )}
    >
      {/* Leading icon */}
      {isValidElement(IconLeading) && IconLeading}
      {isReactComponent(IconLeading) && (
        <IconLeading data-icon="leading" className={styles.common.icon} />
      )}

      {loading && (
        <svg
          fill="none"
          data-icon="loading"
          viewBox="0 0 20 20"
          className={cx(
            styles.common.icon,
            !showTextWhileLoading && 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          )}
        >
          {/* Background circle */}
          <circle
            className="stroke-current opacity-30"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
          />
          {/* Spinning circle */}
          <circle
            className="animate-spin origin-center stroke-current"
            cx="10"
            cy="10"
            r="8"
            fill="none"
            strokeWidth="2"
            strokeDasharray="12.5 50"
            strokeLinecap="round"
          />
        </svg>
      )}

      {children && (
        <span {...safeTextProps} data-text className={cx('transition-inherit-all', !noTextPadding && 'px-0.5')}>
          {children}
        </span>
      )}

      {/* Trailing icon */}
      {isValidElement(IconTrailing) && IconTrailing}
      {isReactComponent(IconTrailing) && (
        <IconTrailing data-icon="trailing" className={styles.common.icon} />
      )}
    </Component>
  );
};
